import {
  KpiMetricType,
  SetRowKpisV2Input
} from '../../../data/gql-gen/questionnaire/graphql'
import {
  DraftQuestionnaireEntry,
  EntryKpi
} from '../../../data/model/questionnaire'
import { getQuestionEntryPrefix } from '../../../utils/questionnaireUtils'
import {
  KpiSubjectKind,
  getAllowedKpiMetrics,
  getKpiMatrixRows,
  getKpiRowLabel,
  getKpiSubjectKind,
  hasTrackingId,
  isMatrixKpiSubject
} from './kpi.utils'
import {
  KpiDialogOption,
  KpiMeasureDraft,
  countsSameAs,
  getKpiDialogOptions,
  getKpiQuestionLine,
  hasDontKnow,
  hasKpiTicksFor,
  rowKpiLabel,
  suggestedKpiMeasure,
  toKpiMeasureOptionIds,
  toggle
} from './kpiDialogModel'
import { rowKpisOf } from './kpiImpact'

export type KpiRowsSaveInput = Omit<
  SetRowKpisV2Input,
  'projectId' | 'waveId' | 'surveyId' | 'entryId' | 'topN'
>

export interface KpiRowsDialogRow {
  id: string
  label: string
  /** A row without one can't be a KPI, so it is listed but can't be ticked. */
  hasTrackingId: boolean
  kpis: EntryKpi[]
}

export interface KpiRowsDialogModel {
  subjectKind: KpiSubjectKind
  contextLine: string
  questionRef: string
  metricTypes: KpiMetricType[]
  options: KpiDialogOption[]
  rows: KpiRowsDialogRow[]
  hasDontKnow: boolean
}

export interface KpiRowsDraft extends KpiMeasureDraft {
  rowIds: string[]
  /** Names typed in, by row id. The rest follow the row's text and the metric. */
  labels: Partial<Record<string, string>>
}

/** What the dialog shows for a matrix, or null for anything else. */
export const getKpiRowsDialogModel = (
  entry: DraftQuestionnaireEntry
): KpiRowsDialogModel | null => {
  const subjectKind = getKpiSubjectKind(entry)
  if (!subjectKind || !isMatrixKpiSubject(subjectKind)) {
    return null
  }
  return {
    subjectKind,
    contextLine: getKpiQuestionLine(entry),
    questionRef: getQuestionEntryPrefix(entry),
    metricTypes: getAllowedKpiMetrics(subjectKind),
    options: getKpiDialogOptions(entry),
    rows: getKpiMatrixRows(entry).map((row) => ({
      id: row.questionLk,
      label: getKpiRowLabel(row),
      hasTrackingId: hasTrackingId(row),
      kpis: rowKpisOf(entry, row.questionLk)
    })),
    hasDontKnow: hasDontKnow(entry)
  }
}

const markableRowIds = (model: KpiRowsDialogModel) =>
  model.rows.filter((row) => row.hasTrackingId).map(({ id }) => id)

/** Every row that can be marked and has no KPI yet starts ticked. */
export const initialKpiRowsDraft = (
  model: KpiRowsDialogModel
): KpiRowsDraft => ({
  ...suggestedKpiMeasure(model),
  rowIds: model.rows
    .filter((row) => row.hasTrackingId && row.kpis.length === 0)
    .map(({ id }) => id),
  labels: {}
})

export const getKpiRowsDraftLabel = (
  draft: KpiRowsDraft,
  row: KpiRowsDialogRow
) => draft.labels[row.id] ?? rowKpiLabel(row.label, draft.metricType)

export const setKpiRowsDraftLabel = (
  draft: KpiRowsDraft,
  rowId: string,
  label: string
): KpiRowsDraft => ({ ...draft, labels: { ...draft.labels, [rowId]: label } })

export const toggleKpiRow = (
  draft: KpiRowsDraft,
  rowId: string
): KpiRowsDraft => ({ ...draft, rowIds: toggle(draft.rowIds, rowId) })

export const setAllKpiRows = (
  model: KpiRowsDialogModel,
  draft: KpiRowsDraft,
  ticked: boolean
): KpiRowsDraft => ({
  ...draft,
  rowIds: ticked ? markableRowIds(model) : []
})

export const setKpiRowsDraftMetric = (
  draft: KpiRowsDraft,
  metricType: KpiMetricType
): KpiRowsDraft => ({ ...draft, metricType })

// In matrix order, without rows that have gone or can't be marked.
const tickedRows = (model: KpiRowsDialogModel, draft: KpiRowsDraft) =>
  model.rows.filter((row) => row.hasTrackingId && draft.rowIds.includes(row.id))

const toMeasureInput = (model: KpiRowsDialogModel, draft: KpiRowsDraft) => ({
  metricType: draft.metricType,
  base: draft.base,
  ...toKpiMeasureOptionIds(model, draft)
})

export const toKpiRowsSaveInput = (
  model: KpiRowsDialogModel,
  draft: KpiRowsDraft
): KpiRowsSaveInput => {
  const rows = tickedRows(model, draft)
  return {
    rowIds: rows.map(({ id }) => id),
    ...toMeasureInput(model, draft),
    // A name left blank is the row's text, as the server names a row it isn't given a name for.
    labels: rows.flatMap(({ id }) => {
      const label = draft.labels[id]?.trim()
      return label ? [{ rowId: id, label }] : []
    })
  }
}

/** The ticked rows that already have a KPI counting exactly this, which the server would refuse. */
export const getDuplicateKpiRows = (
  model: KpiRowsDialogModel,
  draft: KpiRowsDraft
): KpiRowsDialogRow[] => {
  const measure = toMeasureInput(model, draft)
  return tickedRows(model, draft).filter((row) =>
    row.kpis.some((kpi) => countsSameAs(kpi, { ...measure, rowId: row.id }))
  )
}

export const canSaveKpiRows = (
  model: KpiRowsDialogModel,
  draft: KpiRowsDraft
): boolean => {
  const input = toKpiRowsSaveInput(model, draft)
  return (
    input.rowIds.length > 0 &&
    hasKpiTicksFor(input) &&
    getDuplicateKpiRows(model, draft).length === 0
  )
}
